/**
 * @userstory US-MENU-06
 *
 * Route guard that restricts access to Admin-only areas.
 * Members navigating to protected routes (e.g. /company/settings/*)
 * are silently redirected to /company/dashboard (BR-MENU-07).
 */
import React, { FC, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/permissionContext';
import Loading from '../Loading';

interface IAdminProtectedRouteProps {
	children: ReactNode;
	/** Where to redirect non-admin users. Defaults to /company/dashboard */
	redirectPath?: string;
}

/** @userstory US-MENU-06 */
const AdminProtectedRoute: FC<IAdminProtectedRouteProps> = ({
	children,
	redirectPath = '/company/dashboard',
}) => {
	const { isAdmin, isLoading } = usePermissions();

	// Wait for permissions to resolve before deciding. Without this, a direct
	// load / hard refresh of an admin route renders before the auth sync
	// populates memberships, transiently making isAdmin false and bouncing
	// even legitimate admins to the dashboard.
	if (isLoading) {
		return <Loading />;
	}

	// BR-MENU-07: Non-admin users are silently redirected
	if (!isAdmin) {
		return <Navigate to={redirectPath} replace />;
	}

	return <>{children}</>;
};

export default AdminProtectedRoute;
